import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { Textarea } from '../../../../form/textarea';
import { Select } from '../../../../form/select';
import { Main, Styled } from './style';
import { AuthenticateSelect } from '../../../../auth/select';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';

type ShopifyProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

type ShopifyResource = 'order' | 'product' | 'customer';

const resourceOptionsBase: selectDataType[] = [
   { id: 1, name: 'Order', refId: 'order' },
   { id: 2, name: 'Product', refId: 'product' },
   { id: 3, name: 'Customer', refId: 'customer' }
];

const requestOptionsByResource: Record<ShopifyResource, selectDataType[]> = {
   order: [
      { id: 1, name: 'Create Order', refId: 'createOrder' },
      { id: 2, name: 'Get Orders', refId: 'getOrders' },
      { id: 3, name: 'Get Order By ID', refId: 'getOrderById' },
      { id: 4, name: 'Cancel Order', refId: 'cancelOrder' },
      { id: 5, name: 'Update Order', refId: 'updateOrder' },
      { id: 6, name: 'Append Order Note', refId: 'appendOrderNote' },
      { id: 7, name: 'Order Mark As Paid', refId: 'orderMarkAsPaid' },
      { id: 8, name: 'Return Approve Request', refId: 'returnApproveRequest' },
      { id: 9, name: 'Return Decline Request', refId: 'returnDeclineRequest' },
      { id: 10, name: 'Return Create', refId: 'returnCreate' },
      { id: 11, name: 'Refund Create', refId: 'refundCreate' },
      // { id: 8, name: 'Return Process', refId: 'returnProcess' },
      // { id: 11, name: 'Inventory Restock Decision', refId: 'inventoryRestockDecision' }
      // , { id: 12, name: 'Exchange Action', refId: 'exchangeAction' }
   ],
   product: [
      { id: 1, name: 'Create Product', refId: 'createProduct' },
      { id: 2, name: 'Get Product', refId: 'getProduct' },
      { id: 3, name: 'Get Products', refId: 'getProducts' },
      { id: 4, name: 'Update Product', refId: 'updateProduct' },
      { id: 5, name: 'Update Inventory', refId: 'updateInventory' }
   ],
   customer: [
      { id: 1, name: 'Create Customer', refId: 'createCustomer' },
      { id: 2, name: 'Get Customer', refId: 'getCustomer' },
      { id: 3, name: 'Get Customers', refId: 'getCustomers' },
      { id: 4, name: 'Update Customer', refId: 'updateCustomer' }
   ]
};

const defaultResource: ShopifyResource = 'order';
const defaultRequest = requestOptionsByResource[defaultResource][0].refId as string;

function getResourceFromRequest(request?: string): ShopifyResource {
   if (!request) return defaultResource;
   for (const resource of Object.keys(requestOptionsByResource) as ShopifyResource[]) {
      if (requestOptionsByResource[resource].some(option => option.refId === request)) {
         return resource;
      }
   }
   return defaultResource;
}

const functionDocs: { [key: string]: string } = {
   createOrder: [
      'Query: {\n    "email": "qa.orders+001@example.com",\n    "financial_status": "paid",\n    "line_items": [{ "title": "QA Item A", "price": "19.99", "quantity": 1 }],\n    "tags": "qa,function-block"\n  }',
      'Response includes: `id`, `order_number`, `customer`, `line_items`, `total_price`, `financial_status`.'
   ].join('\n'),
   getOrders: [
      'Query: {\n    "status": "any",\n    "limit": 50,\n    "fields": "id,order_number,total_price,financial_status"\n  }',
      'Response includes: `data[]`, `pagination`, `meta`, and each order item includes `id`, `order_number`, `total_price`, `financial_status`.'
   ].join('\n'),
   getOrderById: [
      'Query: {\n    "fields": "id,order_number,total_price,financial_status,customer,line_items",\n    "status": "any"\n  }',
      'Response includes: `data`, `meta`, and order details like `id`, `order_number`, `customer`, `line_items`, `total_price`, `financial_status`.'
   ].join('\n'),
   cancelOrder: [
      'Order ID is required.',
      'Cancel Reason is optional and is saved into the Shopify order note. If omitted, backend records a default customer-requested cancellation reason.',
      '`Notify Customer` defaults to `true`. You can pass `true` or `false`.',
      'Structured outcomes: `CANCELLED`, `PENDING`, `ALREADY_CANCELLED`, `MANUAL_REVIEW_REQUIRED`, `RETURN_REQUIRED`.',
      'Self-service cancellation is only for unpaid and unfulfilled orders.'
   ].join('\n'),
   appendOrderNote: [
      'Shopify does not allow apps to create native Timeline comments through the public Admin API.',
      'This action appends a timestamped entry to the Order Notes field instead.',
      'Provide exactly one identifier: `Order ID` or `Order Name`.',
      'Order Name supports values like `1056`, `#1056`, or custom visible names such as `1001-A`.',
      'Provide `Text`, `Photo URL`, or both. Photo URL must be `http://` or `https://`.'
   ].join('\n'),
   updateOrder: [
      'Query: {\n    "notify_customer": false,\n    "fields": "id,order_number,financial_status,fulfillment_status,total_price"\n  }',
      'Response includes: `data`, `meta`, and updated order fields such as `id`, `order_number`, `financial_status`, `fulfillment_status`, `total_price`.'
   ].join('\n'),
   createCustomer: [
      'Query: {\n    "email": "qa.customer+001@example.com",\n    "first_name": "QA",\n    "last_name": "Customer",\n    "tags": "qa,function-block"\n  }',
      'Response includes: `id`, `email`, `first_name`, `last_name`, `phone`, `state`, `created_at`.'
   ].join('\n'),
   getCustomer: [
      'Customer ID is required.',
      'Query: {\n    "fields": "id,email,first_name,last_name,phone,default_address"\n  }',
      'Response includes: `data`, `meta`, and customer details like `id`, `email`, `first_name`, `last_name`, `phone`, `default_address`.'
   ].join('\n'),
   getCustomers: [
      'Query: {\n    "limit": 50,\n    "fields": "id,email,first_name,last_name,phone,state"\n  }',
      'Response includes: `data[]`, `pagination`, `meta`, and each customer includes `id`, `email`, `first_name`, `last_name`, `phone`, `state`.'
   ].join('\n'),
   updateCustomer: [
      'Query: {\n    "fields": "id,email,first_name,last_name,state,tags"\n  }',
      'Response includes: `data`, `meta`, and updated customer fields like `id`, `email`, `first_name`, `last_name`, `phone`, `state`, `tags`.'
   ].join('\n'),
   createProduct: [
      'Query: {\n    "title": "QA Product A",\n    "vendor": "QA Vendor",\n    "product_type": "QA Type",\n    "tags": "qa,function-block"\n  }',
      'Response includes: `id`, `title`, `handle`, `status`, `variants`, `options`.'
   ].join('\n'),
   getProducts: [
      'Query: {\n    "limit": 50,\n    "fields": "id,title,handle,status,variants",\n    "published_status": "any"\n  }',
      'Response includes: `data[]`, `pagination`, `meta`, and each product includes `id`, `title`, `handle`, `status`, `variants`.'
   ].join('\n'),
   getProduct: [
      'Product ID is required.',
      'Query: {\n    "fields": "id,title,handle,status,variants,updated_at"\n  }',
      'Response includes: `data`, `meta`, and product details such as `id`, `title`, `handle`, `status`, `variants`, `updated_at`.'
   ].join('\n'),
   updateProduct: [
      'Query: {\n    "fields": "id,title,handle,status,variants,updated_at"\n  }',
      'Response includes: `data`, `meta`, and updated product fields such as `id`, `title`, `handle`, `status`, `variants`, `updated_at`.'
   ].join('\n'),
   updateInventory: [
      'Quantity is required and is treated as an absolute available inventory value.',
      'Provide either `Inventory Item ID` or `Variant ID` (Inventory Item ID is preferred when both are provided).',
      'Location ID is optional; if omitted, backend auto-selects the first active location.',
      'Example: Inventory Item ID: `47171389882402`, Quantity: `22`.',
      'Response includes: `inventorySetQuantities.inventoryAdjustmentGroup` and resolved inventory/location IDs in `data.resolvedInput`.'
   ].join('\n'),
   orderMarkAsPaid: [
      'Order ID is required.',
      'Response includes: `orderMarkAsPaid.order.id`, `orderMarkAsPaid.order.displayFinancialStatus`.'
   ].join('\n'),
   returnApproveRequest: [
      'Return ID is required.',
      'Optional Payload: {\n    "notifyCustomer": true\n  }',
      'Response includes: `returnApproveRequest.return.id`, `returnApproveRequest.return.status`.'
   ].join('\n'),
   returnDeclineRequest: [
      'Return ID and Decline Reason are required.',
      'Decline Reason enum: `FINAL_SALE | RETURN_PERIOD_ENDED | OTHER`.',
      'Optional fields: Decline Note, Notify Customer.'
   ].join('\n'),
   returnProcess: [
      'Return ID and Payload are required.',
      'Query: {\n    "returnId": "20122173474",\n    "returnLineItems": [\n      {\n        "id": "6305884322",\n        "quantity": 1,\n        "dispositions": [\n          {\n            "dispositionType": "RESTOCKED",\n            "reverseFulfillmentOrderLineItemId": "9876543210",\n            "quantity": 1,\n            "locationId": "106311319586"\n          }\n        ]\n      }\n    ]\n  }',
      'Payload tip: since `Return ID` has its own field in this node, put only `returnLineItems`, `exchangeLineItems`, and other `ReturnProcessInput` fields in Payload.',
      'ID tip: numeric IDs are accepted; backend auto-converts IDs to Shopify GIDs for `returnLineItems[].id`, `dispositions[].reverseFulfillmentOrderLineItemId`, and `locationId`.',
      'Response includes: `returnProcess.return.id`, `returnProcess.return.status`.'
   ].join('\n'),
   returnCreate: [
      'Order ID and Payload are required & order must   "fulfillment_status": "fulfilled".',
      'Query: {\n    "orderId": "625362839",\n    "returnLineItems": [\n      {\n        "fulfillmentLineItemId": "820022594",\n        "quantity": 1,\n        "returnReason": "SIZE_TOO_SMALL",\n        "returnReasonNote": "Customer requested size exchange"\n      }\n    ]\n  }',
      'Payload tip: since `Order ID` has its own field in this node, put only `returnLineItems` and other `ReturnInput` fields in Payload.',
      'ID tip: numeric IDs are accepted; backend auto-converts IDs to Shopify GIDs.',
      'Response includes: `returnCreate.return.id`, `returnCreate.return.status`, `returnCreate.return.order.id`.'
   ].join('\n'),
   refundCreate: [
      'Order ID and Payload are required.',
      'Query: {\n    "orderId": "6364598304802",\n    "refundLineItems": [\n      {\n        "lineItemId": "15908234330146",\n        "quantity": 1,\n        "restockType": "RETURN"\n      }\n    ],\n    "notify": true,\n    "note": "Approved by reconciliation workflow"\n  }',
      'Payload tip: since `Order ID` has its own field in this node, put only `refundLineItems` and other `RefundInput` fields in Payload.',
      'ID tip: numeric IDs are accepted; backend auto-converts `refundLineItems[].lineItemId` to Shopify GID.',
      'Idempotency key is auto-generated by backend.',
      'Response includes: `refundCreate.refund.id`.'
   ].join('\n'),
   inventoryRestockDecision: [
      'Wrapper over `returnProcess` for restock decisions.',
      'Return ID and Payload are required.',
      'Query: {\n    "returnId": "20122173474",\n    "returnLineItems": [\n      {\n        "id": "6305884322",\n        "quantity": 1,\n        "dispositions": [\n          {\n            "dispositionType": "RESTOCKED",\n            "reverseFulfillmentOrderLineItemId": "9876543210",\n            "quantity": 1,\n            "locationId": "106311319586"\n          }\n        ]\n      }\n    ]\n  }',
      'Payload tip: use restock-focused `dispositions` values like `RESTOCKED`, `NOT_RESTOCKED`, or `DAMAGED` as required by your flow.',
      'ID tip: numeric IDs are accepted; backend auto-converts relevant IDs to Shopify GIDs.',
      'Response includes: `returnProcess.return.id`, `returnProcess.return.status`.'
   ].join('\n')
   // , exchangeAction: [
   //    'Wrapper over `returnProcess` for exchange operations.',
   //    'Return ID and Payload are required.',
   //    'Query: {\n    "returnId": "20122173474",\n    "exchangeLineItems": [\n      {\n        "id": "1470988202",\n        "quantity": 1\n      }\n    ]\n  }',
   //    'Payload tip: for exchange flows, send `exchangeLineItems` in Payload and keep `returnId` in its dedicated field above.',
   //    'ID tip: numeric IDs are accepted; backend auto-converts `exchangeLineItems[].id` to Shopify GIDs.',
   //    'Response includes: `returnProcess.return.id`, `returnProcess.return.status`.'
   // ].join('\n')
};

const declineReasonOptions: selectDataType[] = [
   { id: 1, name: 'Final Sale', refId: 'FINAL_SALE' },
   { id: 2, name: 'Return Period Ended', refId: 'RETURN_PERIOD_ENDED' },
   { id: 3, name: 'Other', refId: 'OTHER' }
];

export function Shopify({ id, data, setChildMinimize, setChildSettings }: ShopifyProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            resource: defaultResource,
            request: defaultRequest,
            payload: '{\n  "title": "Sample"\n}',
            query: '{\n  "limit": 50\n}'
         });
      } else if (!data.value?.resource && data.value?.request) {
         updateNodeValue(id, setNodes, { resource: getResourceFromRequest(data.value.request) });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
      } else {
         updateNodeValue(id, setNodes, { connection: null });
      }
   };

   const activeRequestValue = data.value?.request as string | undefined;
   const activeResourceValue = (data.value?.resource as ShopifyResource | undefined) || getResourceFromRequest(activeRequestValue);
   const requestOptionsForResource = requestOptionsByResource[activeResourceValue];
   const activeRequest = requestOptionsForResource.some(request => request.refId === activeRequestValue)
      ? (activeRequestValue as string)
      : (requestOptionsForResource[0]?.refId as string);

   const resourceOptions = resourceOptionsBase.map(option => ({ ...option, isActive: option.refId === activeResourceValue }));
   const actionOptions = requestOptionsForResource.map(option => ({ ...option, isActive: option.refId === activeRequest }));

   const handleSelectResource = (selectId: number) => {
      const nextResource = resourceOptionsBase.find(resource => resource.id === selectId)?.refId as ShopifyResource | undefined;
      if (!nextResource) return;
      const firstAction = requestOptionsByResource[nextResource][0]?.refId as string | undefined;
      updateNodeValue(id, setNodes, { resource: nextResource, request: firstAction });
   };

   const handleSelectAction = (selectId: number) => {
      const refId = requestOptionsForResource.find(request => request.id === selectId)?.refId;
      if (!refId) return;
      updateNodeValue(id, setNodes, { resource: activeResourceValue, request: refId });
   };

   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   };

   const isOrderIdRequest = ['getOrderById', 'cancelOrder', 'updateOrder', 'orderMarkAsPaid', 'returnCreate', 'refundCreate'].includes(activeRequest);
   const isCancelOrderRequest = activeRequest === 'cancelOrder';
   const isAppendOrderNoteRequest = activeRequest === 'appendOrderNote';
   const isReturnIdRequest = ['returnApproveRequest', 'returnDeclineRequest', 'returnProcess', 'inventoryRestockDecision'
      // , 'exchangeAction'
   ].includes(activeRequest);
   const isCustomerRequest = ['getCustomer', 'updateCustomer'].includes(activeRequest);
   const isProductRequest = ['getProduct', 'updateProduct'].includes(activeRequest);
   const isUpdateInventoryRequest = activeRequest === 'updateInventory';
   const isDeclineRequest = activeRequest === 'returnDeclineRequest';
   const isPayloadRequest = ['createOrder', 'updateOrder', 'createCustomer', 'updateCustomer', 'createProduct', 'updateProduct', 'returnApproveRequest', 'returnProcess', 'returnCreate', 'refundCreate', 'inventoryRestockDecision'
      // , 'exchangeAction'
   ].includes(activeRequest);
   const isQueryRequest = ['getOrders', 'getCustomer', 'getCustomers', 'getProducts', 'getProduct', 'getOrderById'].includes(activeRequest);
   const isWarning = !data.value?.connection;

   const declineOptions = declineReasonOptions.map(option => ({ ...option, isActive: data.value?.declineReason === option.refId }));

   return (
      <>
         <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
         <Main>
            {isWarning ? (
               <>
                  <div id="warning"></div>
                  <span id="warningIcon"><IoWarningOutline size={21} /></span>
               </>
            ) : data.output ? (
               <>
                  <div id="ran"></div>
                  <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
               </>
            ) : null}

            {isExpanded ? (
               <ExpandedBlock
                  data={{ id, ...data }}
                  handleClose={ () => setIsExpanded(false) }
                  isWarning={ isWarning }
                  style={{ minWidth: '420px' }}
               >
                  <Styled>
                     <div className="action">
                        <AuthenticateSelect
                           type="shopify"
                           defaultId={ data.value?.connection?.id }
                           handleAuthenticate={ handleAuthenticate }
                           background={ data.background }
                        />
                     </div>

                     <Select title="Resource" data={ resourceOptions } handleSelectItem={ handleSelectResource } isSmall={ true } />
                     <Select title="Action" data={ actionOptions } handleSelectItem={ handleSelectAction } isSmall={ true } />

                     {isOrderIdRequest ? (
                        <TextInput
                           keyRef="orderId"
                           value={ data.value?.orderId }
                           title="Order ID"
                           placeholder="450789469"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isAppendOrderNoteRequest ? (
                        <>
                           <TextInput
                              keyRef="orderId"
                              value={ data.value?.orderId }
                              title="Order ID (Optional)"
                              placeholder="450789469"
                              handleChange={ handleChangeInput }
                           />
                           <TextInput
                              keyRef="orderName"
                              value={ data.value?.orderName }
                              title="Order Name (Optional)"
                              placeholder="#1056 or 1001-A"
                              handleChange={ handleChangeInput }
                           />
                           <Textarea
                              title="Text"
                              keyRef="text"
                              value={ data.value?.text }
                              placeholder={'Customer shared more return details'}
                              handleChange={ (e) => updateNodeValue(id, setNodes, { text: e.target.value }) }
                           />
                           <TextInput
                              keyRef="photoUrl"
                              value={ data.value?.photoUrl }
                              title="Photo URL (Optional)"
                              placeholder="https://example.com/photo.jpg"
                              handleChange={ handleChangeInput }
                           />
                        </>
                     ) : null}

                     {isCancelOrderRequest ? (
                        <>
                           <TextInput
                              keyRef="cancelReason"
                              value={ data.value?.cancelReason }
                              title="Cancel Reason (Optional)"
                              placeholder="Customer requested cancellation before shipment"
                              handleChange={ handleChangeInput }
                           />
                           <TextInput
                              keyRef="notifyCustomer"
                              value={ data.value?.notifyCustomer }
                              title="Notify Customer (Optional)"
                              placeholder="true"
                              handleChange={ handleChangeInput }
                           />
                        </>
                     ) : null}

                     {isReturnIdRequest ? (
                        <TextInput
                           keyRef="returnId"
                           value={ data.value?.returnId }
                           title="Return ID"
                           placeholder="945000959"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isCustomerRequest ? (
                        <TextInput
                           keyRef="customerId"
                           value={ data.value?.customerId }
                           title="Customer ID"
                           placeholder="207119551"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isProductRequest ? (
                        <TextInput
                           keyRef="productId"
                           value={ data.value?.productId }
                           title="Product ID"
                           placeholder="632910392"
                           handleChange={ handleChangeInput }
                        />
                     ) : null}

                     {isUpdateInventoryRequest ? (
                        <>
                           <TextInput
                              keyRef="inventoryItemId"
                              value={ data.value?.inventoryItemId }
                              title="Inventory Item ID"
                              placeholder="47171389882402"
                              handleChange={ handleChangeInput }
                           />
                           <TextInput
                              keyRef="variantId"
                              value={ data.value?.variantId }
                              title="Variant ID (Optional)"
                              placeholder="45087287181346"
                              handleChange={ handleChangeInput }
                           />
                           <TextInput
                              keyRef="locationId"
                              value={ data.value?.locationId }
                              title="Location ID (Optional)"
                              placeholder="106311319586"
                              handleChange={ handleChangeInput }
                           />
                           <TextInput
                              keyRef="quantity"
                              value={ data.value?.quantity }
                              title="Quantity"
                              placeholder="22"
                              handleChange={ handleChangeInput }
                           />
                        </>
                     ) : null}

                     {isDeclineRequest ? (
                        <>
                           <Select
                              title="Decline Reason"
                              data={ declineOptions }
                              handleSelectItem={ (selectId) => {
                                 const declineReason = declineReasonOptions.find(option => option.id === selectId)?.refId;
                                 updateNodeValue(id, setNodes, { declineReason });
                              } }
                              isSmall={ true }
                           />
                           <TextInput
                              keyRef="declineNote"
                              value={ data.value?.declineNote }
                              title="Decline Note (Optional)"
                              placeholder="Reason shown to customer"
                              handleChange={ handleChangeInput }
                           />
                        </>
                     ) : null}

                     {isPayloadRequest ? (
                        <Textarea
                           title="Payload"
                           keyRef="payload"
                           value={ data.value?.payload }
                           placeholder={'{\n  "title": "Sample"\n}'}
                           handleChange={ (e) => updateNodeValue(id, setNodes, { payload: e.target.value }) }
                        />
                     ) : null}

                     {isQueryRequest ? (
                        <Textarea
                           title="Query"
                           keyRef="query"
                           value={ data.value?.query }
                           placeholder={'{\n  "limit": 50,\n  "fields": "id,name"\n}'}
                           handleChange={ (e) => updateNodeValue(id, setNodes, { query: e.target.value }) }
                        />
                     ) : null}

                     <div className="previewSection">
                        <h5>Function Block Preview</h5>
                        <pre>{ functionDocs[activeRequest] || 'No function metadata available.' }</pre>
                     </div>
                  </Styled>
               </ExpandedBlock>
            ) : null}
         </Main>
      </>
   );
}
